import "./LandholdingWithTabsTemplate.css";

import { ArrowFullLeftDarkIcon } from "../../assets";
import { InteractionObjectType, OrganisationTypeConstants, ProjectGroupPermission, UserType } from "../../constants";
import { NavigationActiveElement, SelectData, TabsList } from "../../models";
import { DashboardPieCard } from "../../route/shared/components";
import {
  Button,
  Card,
  ChartData,
  Column,
  InformationBox,
  IssuanceStatusFilter,
  KanaTooltip,
  LeftNavigationBar,
  LocationFlag,
  OrganisationIcon,
  PieChart,
  Row,
  ScrollablePills,
  SeedFooter,
  SeedNavigation,
  Select,
  Tabs,
  UserHeader,
} from "../../widget";
import { TagsPanel } from "../TagsPanel";
import { useLandholdingWithTabsTemplate } from "./useLandholdingWithTabsTemplate";

interface DeveloperInfo {
  name?: string;
  icon?: string;
}

interface LandholdingWithTabsTemplateProps {
  activeElement: NavigationActiveElement;
  landholdingUuid?: string;
  pageTitle: string | undefined;
  addressCountryCode?: string;
  developers: DeveloperInfo[]; // Ensure distinct developers
  tabsList: TabsList;
  backButtonText?: string;
  handleBackClick?: () => void;
  quickActions?: JSX.Element;
  currentUserType?: UserType;
  projectsChartData: ChartData;
  areaChartData: ChartData;
  projectsChartColours: string[][];
  areaChartColours: string[][];
  isInventoryChartLoading: boolean;
  inventoryChartData: ChartData;
  selectedStatuses: string[];
  onStatusesChange: (values: string[]) => void;
  hasFullProjectAccess: boolean;
  selectedUnitMeasurementData: string;
  updateInventoryChartData: (unitMeasurement: string) => void;
  unitMeasurementDropDownData: SelectData;
  readGroupProjectsPermission: ProjectGroupPermission | undefined;
}

export const LandholdingWithTabsTemplate = ({
  activeElement,
  landholdingUuid,
  pageTitle,
  addressCountryCode,
  developers,
  tabsList,
  backButtonText,
  handleBackClick,
  quickActions,
  currentUserType,
  projectsChartData,
  areaChartData,
  projectsChartColours,
  areaChartColours,
  isInventoryChartLoading,
  inventoryChartData,
  selectedStatuses,
  onStatusesChange,
  hasFullProjectAccess,
  selectedUnitMeasurementData,
  updateInventoryChartData,
  unitMeasurementDropDownData,
  readGroupProjectsPermission,
}: LandholdingWithTabsTemplateProps): JSX.Element => {
  const { showTagsPanel, setShowTagsPanel, shouldRefreshTags, tags } = useLandholdingWithTabsTemplate(landholdingUuid);

  return (
    <div className="LandholdingTemplate">
      {currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? (
        <SeedNavigation activeElement="Projects" />
      ) : (
        <div className="LandholdingTemplateNavigation">
          <LeftNavigationBar activeItem={activeElement} />
        </div>
      )}
      <div className={`LandholdingTemplateContainer ${currentUserType}`}>
        {currentUserType !== OrganisationTypeConstants.ASSET_MANAGER ? (
          <UserHeader backButtonText={backButtonText} handleBackClick={handleBackClick} />
        ) : (
          <div className="HeaderContainer">
            <Button
              text={backButtonText ?? "Back"}
              variant="secondary"
              icon={<ArrowFullLeftDarkIcon />}
              iconPosition="left"
              onClick={handleBackClick}
              hasNoPadding
              minWidth={false}
            />
          </div>
        )}
        <Row spacingV={developers.length > 0 ? "ll" : "s"}>
          <Column span={12} className="TitleAndPill">
            <h1>{pageTitle}</h1>
            {addressCountryCode && <LocationFlag addressCountryCode={addressCountryCode} />}
            {quickActions !== undefined && readGroupProjectsPermission !== ProjectGroupPermission.NONE && (
              <div className="LandholdingQuickActions">{quickActions}</div>
            )}
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={8}>
            <div className="DevelopersContainer">
              {developers.map(({ name, icon }) => (
                <div key={name} className="DeveloperInfo">
                  {icon && <OrganisationIcon organisationIcon={icon} />}
                  <p className="body1">{name}</p>
                </div>
              ))}
            </div>
          </Column>
          <Column span={4} justify="end">
            <ScrollablePills pills={tags} linkText="Tags" linkOnClick={() => setShowTagsPanel(true)} />
          </Column>
        </Row>
        {readGroupProjectsPermission !== ProjectGroupPermission.FULL && (
          <Row spacingV="l">
            <Column span={12}>
              <InformationBox variant="info" title="Limited access" corners="rounded" hasBorder>
                <p className="body1">
                  Your organisation does not have access to all projects within this landholding, so you will only see
                  data for projects you can access. To request access to other projects, click a project link in the
                  table below and select “Request access” in the grey banner.
                </p>
              </InformationBox>
            </Column>
          </Row>
        )}
        <Row spacingV="ml">
          <Column span={12}>
            <div className="LandholdingDashboardKpis">
              {readGroupProjectsPermission !== ProjectGroupPermission.NONE ? (
                <Card
                  padding="l"
                  corners={currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "rounded" : "default"}
                >
                  <Row>
                    <Column span={12} hasNoPadding>
                      {/* Title and both controls share one line. The card is a third of the
                          dashboard width, so the title keeps its intrinsic width and the two
                          dropdowns split whatever is left. */}
                      <div className="LandholdingInventoryKpiHeader">
                        <div className="LandholdingInventoryKpiTitle">
                          <p className="body01">Inventory</p>
                          {!hasFullProjectAccess && (
                            <KanaTooltip
                              tooltipHeader="Inventory:"
                              tooltipText="This figure covers issued units only. Your organisation does not have access to every project on this landholding, so draft and inactive units are not included."
                            />
                          )}
                        </div>
                        <div className="LandholdingInventoryKpiControls">
                          {hasFullProjectAccess && (
                            <div className="LandholdingInventoryKpiStatusFilter">
                              <IssuanceStatusFilter values={selectedStatuses} onChange={onStatusesChange} />
                            </div>
                          )}
                          <Select
                            value={selectedUnitMeasurementData}
                            data={unitMeasurementDropDownData}
                            onChange={(value) => updateInventoryChartData(value)}
                          />
                        </div>
                      </div>
                    </Column>
                  </Row>
                  <Row>
                    <Column span={12}>
                      <div className="InventoryChart">
                        {!isInventoryChartLoading && (
                          <PieChart
                            size={120}
                            data={inventoryChartData}
                            isGradient
                            emphasis="Secondary"
                            showValues
                            defaultLegendValue="0"
                            noDataMessage="No inventory data"
                          />
                        )}
                      </div>
                    </Column>
                  </Row>
                </Card>
              ) : (
                <DashboardPieCard
                  data={inventoryChartData}
                  title="Inventory"
                  isGradient
                  corners={currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "rounded" : "default"}
                  noDataMessage="No inventory data"
                  tooltip={<KanaTooltip tooltipHeader="" tooltipText="Shows the total units pending and verified" />}
                />
              )}
              <DashboardPieCard
                data={projectsChartData}
                title="Projects"
                isGradient
                chartColorsOverride={projectsChartColours}
                corners={currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "rounded" : "default"}
              />
              <DashboardPieCard
                data={areaChartData}
                title="Area"
                isGradient
                centerTextSuffix="ha"
                chartColorsOverride={areaChartColours}
                corners={currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "rounded" : "default"}
              />
            </div>
          </Column>
        </Row>
        <Tabs className="TemplateTabs" variant="tertiary" tabsList={tabsList} currentUserType={currentUserType} />
        {currentUserType === OrganisationTypeConstants.ASSET_MANAGER && <SeedFooter />}
      </div>
      {landholdingUuid && (
        <TagsPanel
          objectUuid={landholdingUuid}
          objectType={InteractionObjectType.LANDHOLDING}
          show={showTagsPanel}
          onClose={() => setShowTagsPanel(false)}
          shouldRefreshTags={shouldRefreshTags}
        />
      )}
    </div>
  );
};
